import React, { useMemo } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useGov } from "@/hooks/governance";
import { adaFromLovelace } from "@/lib/governance";
import { formatAdaBigCount } from "@/lib/format";
import { Badge } from "@/components/primitives";
import { Table } from "@/components/Table";
import { GovPageShell, GovCard, GovSection, GovError, GovSkeleton, GovEmpty } from "@/components/governance/GovShell";

const adaNum = (lovelace) => {
  try { return Number(BigInt(lovelace) / 1000000n); } catch { return 0; }
};

const Stat = ({ label, value, testid }) => (
  <GovCard>
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">{label}</div>
    <div data-testid={testid} className="text-[18px] mono mt-1 text-foreground break-all">{value}</div>
  </GovCard>
);

export const Treasury = ({ meta }) => {
  const { data, error, loading, refetch } = useGov("/governance/treasury");
  const epochs = useMemo(() => (Array.isArray(data?.epochs) ? data.epochs : []), [data]);
  const latest = epochs.length ? epochs[epochs.length - 1] : null;
  const chartData = epochs
    .filter((e) => e.data_status === "available")
    .map((e) => ({ epoch: e.epoch, treasury: adaNum(e.treasury), reserves: adaNum(e.reserves) }));

  const columns = [
    { key: "epoch", label: "Epoch", render: (r) => <span className="text-foreground">{r.epoch}</span> },
    { key: "treasury", label: "Treasury", align: "right", render: (r) => (r.data_status === "available" ? `${formatAdaBigCount(r.treasury)} ₳` : <span className="text-amber-300/80">{r.unavailable_reason || "n/a"}</span>) },
    { key: "reserves", label: "Reserves", align: "right", render: (r) => (r.data_status === "available" ? `${formatAdaBigCount(r.reserves)} ₳` : "—") },
    { key: "wd", label: "Withdrawn", align: "right", render: (r) => (Number(r.withdrawals_enacted) > 0 ? <span className="text-cyan-300">{formatAdaBigCount(r.withdrawals_enacted)} ₳</span> : <span className="text-muted-foreground">0</span>) },
    { key: "wdn", label: "Payouts", align: "right", render: (r) => <span className="text-muted-foreground">{r.withdrawal_proposals_enacted ?? 0}</span> },
  ];

  return (
    <GovPageShell crumbs={meta.crumbs} title={meta.title} subtitle="On-chain treasury and reserves balance, with enacted withdrawals per epoch.">
      {error ? (
        <GovError error={error} onRetry={refetch} />
      ) : loading && !data ? (
        <GovSkeleton lines={8} />
      ) : latest ? (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat testid="treasury-balance" label={`Treasury · epoch ${latest.epoch}`} value={latest.data_status === "available" ? `${adaFromLovelace(latest.treasury)}` : (latest.unavailable_reason || "unavailable")} />
            <Stat testid="treasury-reserves" label="Reserves" value={latest.data_status === "available" ? `${adaFromLovelace(latest.reserves)}` : "—"} />
            <Stat testid="treasury-range" label="Window" value={`epoch ${data.from_epoch}–${data.to_epoch}`} />
          </div>

          {chartData.length > 1 && (
            <GovCard className="pt-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mono mb-3">Treasury Balance (₳)</div>
              <div style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="treasuryArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" opacity={0.3} vertical={false} />
                    <XAxis dataKey="epoch" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                    <YAxis width={48} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}M`} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => [`${Number(v).toLocaleString()} ₳`, "Treasury"]}
                      labelFormatter={(l) => `Epoch ${l}`}
                    />
                    <Area type="monotone" dataKey="treasury" stroke="hsl(var(--primary))" strokeWidth={1.5} fill="url(#treasuryArea)" isAnimationActive={false} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GovCard>
          )}

          <GovSection title="Per-Epoch Ledger" subtitle={data.source ? `Source: ${data.source}` : undefined}>
            <Table columns={columns} rows={[...epochs].reverse()} empty="No treasury data." />
          </GovSection>
        </>
      ) : <GovEmpty>Treasury data unavailable.</GovEmpty>}
    </GovPageShell>
  );
};
